import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { Form } from "react-bootstrap";
import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
export interface SearchSelectOption {
  value: string;
  label: string;
}

// A heading with its options, shown as a group in the menu.
export interface SearchSelectGroup {
  label: string;
  options: SearchSelectOption[];
}

interface SearchSelectBaseProps {
  id: string;
  label: string;
  // Keeps the label for screen readers without showing it (e.g. in a table cell).
  hideLabel?: boolean;
  options: SearchSelectOption[] | SearchSelectGroup[];
  className?: string;
  // From useInfiniteSelectOptions: server search and next-page loading.
  onInputChange?: (input: string) => void;
  onMenuScrollToBottom?: () => void;
  isLoading?: boolean;
  // Lets the user add a typed name that isn't in the list (value "").
  isCreatable?: boolean;
  // Shows the X that clears the selection.
  isClearable?: boolean;
  required?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  // Gates the "Create" row for typed text (creatable mode only).
  isValidNewOption?: (
    input: string,
    value: readonly SearchSelectOption[],
    options: readonly SearchSelectOption[]
  ) => boolean;
  formatCreateLabel?: (input: string) => string;
}

interface SearchSelectSingleProps extends SearchSelectBaseProps {
  isMulti?: false;
  value: SearchSelectOption | null;
  onChange: (option: SearchSelectOption | null) => void;
}

// Multi mode: a created option is { value: name, label: name } (no id yet),
// and onChange always receives an array, never null.
interface SearchSelectMultiProps extends SearchSelectBaseProps {
  isMulti: true;
  value: SearchSelectOption[];
  onChange: (options: SearchSelectOption[]) => void;
}

export type SearchSelectProps =
  | SearchSelectSingleProps
  | SearchSelectMultiProps;

// react-select has no input the browser can validate. A required select
// carries an invisible required input holding its value, so the browser's
// "Please fill out this field" check covers it like the form's other fields.
// It can't be display:none; the browser won't point its message at that.
const VALIDATION_INPUT_STYLE = {
  position: "absolute",
  left: 0,
  bottom: 0,
  width: "100%",
  height: 1,
  opacity: 0,
  pointerEvents: "none",
} as const;

/**
 * The select to use in new forms: a labelled react-select that can search the
 * API (with useInfiniteSelectOptions), create a typed option, clear, and take
 * part in the browser's required-field check. Prefer it over Select2V2 and
 * RequiredReactSelect.
 */
const SearchSelect = (props: SearchSelectProps): JSX.Element => {
  const {
    id,
    label,
    hideLabel = false,
    isCreatable = false,
    required = false,
    isValidNewOption,
    ...selectProps
  } = props;
  const SelectComponent = isCreatable ? CreatableSelect : Select;
  const validationValue = props.isMulti
    ? props.value.map((option) => option.label).join(", ")
    : props.value?.label ?? "";

  // react-select's generics can't follow the union, so each mode is handled
  // explicitly here.
  const handleChange = (selected: unknown) => {
    if (props.isMulti) {
      props.onChange((selected as SearchSelectOption[] | null) ?? []);
    } else {
      props.onChange(selected as SearchSelectOption | null);
    }
  };
  const handleCreate = (name: string) => {
    if (props.isMulti) {
      props.onChange([...props.value, { value: name, label: name }]);
    } else {
      props.onChange({ value: "", label: name });
    }
  };

  const Wrapper = hideLabel ? "div" : Form.Group;

  return (
    <Wrapper
      {...(!hideLabel && { className: "formGroup" })}
      style={{ position: "relative" }}
    >
      {!hideLabel && <FormFieldLabel className="" label={label} htmlFor={id} />}
      <SelectComponent<SearchSelectOption, boolean, SearchSelectGroup>
        {...selectProps}
        inputId={id}
        {...(hideLabel && { "aria-label": label })}
        onChange={handleChange}
        {...(isCreatable && { onCreateOption: handleCreate })}
        {...(isCreatable &&
          isValidNewOption && {
            // Creatable selects aren't given grouped options, so the cast is safe.
            isValidNewOption: (input, selected, all) =>
              isValidNewOption(
                input,
                selected,
                all as readonly SearchSelectOption[]
              ),
          })}
      />
      {required && (
        <input
          tabIndex={-1}
          aria-hidden
          autoComplete="off"
          required
          value={validationValue}
          onChange={() => undefined}
          style={VALIDATION_INPUT_STYLE}
        />
      )}
    </Wrapper>
  );
};

export default SearchSelect;
